import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, ReactNode, useRef, useState } from 'react';
import { Logger } from '@/app/utils/logger';
import { getPerformanceTimingService, PERFORMANCE_MARKS } from '@/app/services/performance/PerformanceTimingService';

type LoadingContextType = {
  isAppLoading: boolean;
  setMusicPlayerReady: (ready: boolean) => void;
  setBackgroundImageLoaded: (loaded: boolean) => void;
};

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

const LOG_TAG = 'loading-context';
const isWebDriverEnvironment = typeof navigator !== 'undefined' && navigator.webdriver;

type LoadingStatus = 'initializing' | 'ready' | 'timeoutReady';

type LoadingState = {
  status: LoadingStatus;
  musicPlayerReady: boolean;
  backgroundImageLoaded: boolean;
};

type LoadingAction =
  | { type: 'setMusicPlayerReady'; ready: boolean }
  | { type: 'setBackgroundImageLoaded'; loaded: boolean }
  | { type: 'timeoutReady' };

const initialLoadingState: LoadingState = {
  status: isWebDriverEnvironment ? 'timeoutReady' : 'initializing',
  musicPlayerReady: false,
  backgroundImageLoaded: false,
};

const resolveStatus = (state: LoadingState): LoadingState => {
  if (state.status === 'timeoutReady') {
    return state;
  }

  if (state.musicPlayerReady && state.backgroundImageLoaded) {
    return { ...state, status: 'ready' };
  }

  return { ...state, status: 'initializing' };
};

const loadingReducer = (state: LoadingState, action: LoadingAction): LoadingState => {
  switch (action.type) {
    case 'setMusicPlayerReady': {
      if (state.musicPlayerReady === action.ready) {
        return state;
      }
      return resolveStatus({ ...state, musicPlayerReady: action.ready });
    }
    case 'setBackgroundImageLoaded': {
      if (state.backgroundImageLoaded === action.loaded) {
        return state;
      }
      return resolveStatus({ ...state, backgroundImageLoaded: action.loaded });
    }
    case 'timeoutReady': {
      if (state.status === 'ready' || state.status === 'timeoutReady') {
        return state;
      }
      return { ...state, status: 'timeoutReady' };
    }
    default:
      return state;
  }
};

interface LoadingProviderProps {
  children: ReactNode;
}

const MIN_LOADING_MS = 500;

export const LoadingProvider: React.FC<LoadingProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(loadingReducer, initialLoadingState);
  const internallyReady = state.status !== 'initializing';
  const [visiblyLoading, setVisiblyLoading] = useState(!isWebDriverEnvironment);
  const mountTimeRef = useRef(Date.now());
  const bootstrapEndMarked = useRef(false);
  const isAppLoading = visiblyLoading;

  // Debug logging
  useEffect(() => {
    Logger.debug('Loading state updated', LOG_TAG, {
      status: state.status,
      musicPlayerReady: state.musicPlayerReady,
      backgroundImageLoaded: state.backgroundImageLoaded,
      isAppLoading,
    });
  }, [state.status, state.musicPlayerReady, state.backgroundImageLoaded, isAppLoading]);

  // Enforce minimum loading screen display time
  useEffect(() => {
    if (!internallyReady) return;
    const elapsed = Date.now() - mountTimeRef.current;
    const remaining = Math.max(0, MIN_LOADING_MS - elapsed);
    const timer = setTimeout(() => setVisiblyLoading(false), remaining);
    return () => clearTimeout(timer);
  }, [internallyReady]);

  // Mark app bootstrap end when status transitions to ready or timeoutReady
  useEffect(() => {
    if ((state.status === 'ready' || state.status === 'timeoutReady') && !bootstrapEndMarked.current) {
      bootstrapEndMarked.current = true;
      getPerformanceTimingService().mark(PERFORMANCE_MARKS.APP_BOOTSTRAP_END);
      Logger.debug('App bootstrap end marked', LOG_TAG, {
        bootstrapDuration: getPerformanceTimingService().getBootstrapDuration(),
      });
    }
  }, [state.status]);

  // Fallback timeout to prevent infinite loading (10 seconds)
  useEffect(() => {
    if (state.status !== 'initializing') {
      return;
    }

    const timeout = setTimeout(() => {
      Logger.warn('Loading timeout reached, forcing app to load', LOG_TAG);
      dispatch({ type: 'timeoutReady' });
    }, 10000);

    return () => clearTimeout(timeout);
  }, [state.status]);

  const setMusicPlayerReady = useCallback((ready: boolean) => {
    Logger.debug('Setting music player ready', LOG_TAG, { ready });
    dispatch({ type: 'setMusicPlayerReady', ready });
  }, []);

  const setBackgroundImageLoaded = useCallback((loaded: boolean) => {
    Logger.debug('Setting background image loaded', LOG_TAG, { loaded });
    dispatch({ type: 'setBackgroundImageLoaded', loaded });
  }, []);

  const value = useMemo<LoadingContextType>(
    () => ({
      isAppLoading,
      setMusicPlayerReady,
      setBackgroundImageLoaded,
    }),
    [isAppLoading, setMusicPlayerReady, setBackgroundImageLoaded],
  );

  return (
    <LoadingContext.Provider value={value}>
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoading = (): LoadingContextType => {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};